import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db, functions } from '../config/firebase';
import { logAuditEvent, AUDIT_EVENTS } from './auditService';

// Rejects (throws) if this IP has hit the daily signup cap — checked BEFORE
// creating the Firebase Auth account, so an over-the-limit signup never even
// creates a user. Any failure OTHER than an actual rate-limit rejection
// (network blip, cold start, etc.) fails open — a broken check should never
// block a legitimate signup.
async function checkSignupAllowed() {
  try {
    const check = httpsCallable(functions, 'checkSignupAllowed');
    await check();
  } catch (error) {
    if (error.code === 'functions/resource-exhausted') throw error;
  }
}

// Sign up for a 2-week free trial
export const signUpTrial = async (email, displayName, password) => {
  try {
    await checkSignupAllowed();

    const tempPassword = password || (
      (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36) + Math.random().toString(36).slice(2)
    );

    const userCredential = await createUserWithEmailAndPassword(auth, email, tempPassword);
    const user = userCredential.user;

    await updateProfile(user, { displayName });

    const trialExpiresAt = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
    await setDoc(doc(db, 'users', user.uid), {
      email,
      displayName,
      accountType: 'trial',
      trialExpiresAt,
      createdAt: new Date(),
      updatedAt: new Date(),
      isActive: true,
    });

    logAuditEvent(user.uid, AUDIT_EVENTS.SIGN_UP, { accountType: 'trial' });
    return { success: true, user };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Sign up with email and password
export const signUp = async (email, password, displayName, clinic = null) => {
  try {
    await checkSignupAllowed();

    // Create authentication user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Update profile
    await updateProfile(user, { displayName });

    // Create user document in Firestore
    const trialExpiresAt = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
    await setDoc(doc(db, 'users', user.uid), {
      email,
      displayName,
      accountType: 'trial',
      trialExpiresAt,
      role: 'therapist',
      clinic: clinic || null,
      createdAt: new Date(),
      updatedAt: new Date(),
      avatar: null,
      isActive: true,
    });

    logAuditEvent(user.uid, AUDIT_EVENTS.SIGN_UP, { accountType: 'trial' });
    return { success: true, user };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Sign in with email and password
export const signIn = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    logAuditEvent(userCredential.user.uid, AUDIT_EVENTS.SIGN_IN);
    return { success: true, user: userCredential.user };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Sign out
export const logOut = async () => {
  try {
    const uid = auth.currentUser?.uid;
    await signOut(auth);
    if (uid) logAuditEvent(uid, AUDIT_EVENTS.SIGN_OUT);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Send password reset email
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get current authenticated user
export const getCurrentUser = () => {
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });
};

// Get user data from Firestore
export const getUserData = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      return { success: true, data: userDoc.data() };
    }
    return { success: false, error: 'User not found' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Update user profile — only allow safe display fields, never accountType or role
export const updateUserProfile = async (uid, updates) => {
  const ALLOWED = ['displayName', 'jobTitle', 'clinic', 'organization', 'photoURL'];
  const safe = Object.fromEntries(
    Object.entries(updates).filter(([k]) => ALLOWED.includes(k))
  );
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, { ...safe, updatedAt: new Date() });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Subscribe to auth state changes
export const subscribeToAuthChanges = (callback) => {
  return onAuthStateChanged(auth, callback);
};
